/**
 * Theme Provider — reads from BrandingContext and applies all CSS variables.
 *
 * Following the PaaS AppThemeProvider pattern:
 * - Reads TenantBranding from BrandingContext (not direct API fetch)
 * - brandingToPalette() converts TenantBranding -> ColorPalette
 * - On branding change OR mode toggle: updates ALL CSS variables on document.documentElement
 * - Updates favicon dynamically (supports data: URIs and file paths)
 * - Updates document.title from portalTitle
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { ColorPalette, ThemeContextType } from '@/types/theme';
import { SYSTEM_DEFAULT_BRANDING } from '@/lib/branding-defaults';
import type { TenantBranding, BrandingColors } from '@/lib/branding-defaults';
import { hexToHsl } from '@/lib/theme-utils';
import { useBranding } from '@/contexts/branding-context';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'argus_iq_theme_mode';

// Purple SVG fallback favicon (ArgusAI hexagon)
const ARGUS_SVG_FAVICON = `data:image/svg+xml,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
  <path d="M50 5 L90 27.5 L90 72.5 L50 95 L10 72.5 L10 27.5 Z" fill="#8B5CF6" />
  <path d="M50 20 L75 75 L65 75 L50 40 L35 75 L25 75 Z" fill="white" />
  <rect x="40" y="55" width="20" height="5" fill="white" />
</svg>
`)}`;

/**
 * Converts TenantBranding (flat color model) to ColorPalette (nested model used by theme context).
 */
function brandingToPalette(branding: TenantBranding, mode: 'light' | 'dark'): ColorPalette {
  const bc: BrandingColors = mode === 'dark' ? branding.dark : branding.light;

  return {
    name: 'Active Theme',
    mode,
    colors: {
      primary: {
        main: bc.primary,
        dark: bc.primary,
        light: bc.primary,
        lightest: bc.primary,
      },
      neutral: {
        bgPrimary: bc.card,
        bgSecondary: bc.background,
        bgTertiary: bc.card,
        textPrimary: bc.text,
        textSecondary: bc.text,
        textDisabled: bc.text,
        border: bc.card,
      },
      semantic: {
        success: '#52C41A',
        warning: '#FAAD14',
        error: '#F5222D',
        info: bc.primary,
      },
      sidebar: {
        background: bc.sidebarBackground,
        text: bc.sidebarText,
        textSecondary: bc.sidebarText,
        active: bc.sidebarActive,
        activeText: bc.sidebarActiveText,
        hover: bc.sidebarHover,
        hoverText: bc.sidebarText,
        divider: bc.card,
      },
    },
  };
}

/**
 * Updates favicon in the DOM. Supports data: URIs (from upload) and file paths.
 *
 * Wave 1c W1c-08 — accepts an `effectiveVersion` so cache-bust query
 * strings can be appended to URLs that point at a server endpoint
 * (e.g., `/api/v1/branding/assets/<id>`). data: URIs and the SVG fallback
 * are content-addressed and never get a cache-buster.
 */
function updateFavicon(url: string | null, effectiveVersion?: number): void {
  let iconUrl = url;

  const isCustomUpload = iconUrl && iconUrl.startsWith('data:');
  const isDefaultPath = !iconUrl || iconUrl === '/favicon.png' || iconUrl === '/favicon.ico';

  if (isDefaultPath) {
    iconUrl = ARGUS_SVG_FAVICON;
  }

  let finalIconUrl = iconUrl || ARGUS_SVG_FAVICON;

  // Append ?v=<effectiveVersion> when the URL is a real server resource
  // (not a data: URI, not the SVG fallback). Browsers cache favicons
  // aggressively; without the cache-buster a tenant change wouldn't
  // surface until the browser was restarted.
  if (
    typeof effectiveVersion === 'number' &&
    !finalIconUrl.startsWith('data:') &&
    !finalIconUrl.includes('?v=')
  ) {
    const sep = finalIconUrl.includes('?') ? '&' : '?';
    finalIconUrl = `${finalIconUrl}${sep}v=${effectiveVersion}`;
  }

  const iconType = isCustomUpload
    ? 'image/png'
    : finalIconUrl.includes('svg')
      ? 'image/svg+xml'
      : 'image/png';

  const faviconLink = document.querySelector("link[rel='icon']") as HTMLLinkElement | null;
  if (faviconLink) {
    faviconLink.type = iconType;
    faviconLink.href = finalIconUrl;
  }

  const appleTouchIconLink = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement | null;
  if (appleTouchIconLink) {
    appleTouchIconLink.href = finalIconUrl;
  }
}

/**
 * Sets all CSS variables on document.documentElement from branding colors.
 *
 * When using default branding (useDefaultTheme=true or SYSTEM_DEFAULT_BRANDING),
 * we CLEAR all inline style overrides and let the CSS @theme + .dark blocks
 * handle colors. This ensures the OKLCH values from index.css are used
 * (they are perceptually better than hex→HSL conversion).
 *
 * Only when custom tenant branding is active do we override with inline styles.
 */
function applyCssVariables(bc: BrandingColors, mode: 'light' | 'dark', isDefault: boolean = false): void {
  const root = document.documentElement;

  // Dark class toggle — always set this
  if (mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  // If using default branding, clear ALL inline overrides and let CSS handle it
  if (isDefault) {
    clearAllInlineOverrides(root);
    return;
  }

  // Custom branding: override CSS variables with tenant's hex colors
  // Only --color-* prefix is used by Tailwind CSS v4
  const set = (name: string, hex: string) => {
    const hslValue = `hsl(${hexToHsl(hex)})`;
    root.style.setProperty(`--color-${name}`, hslValue);
  };

  // Base variables
  set('primary', bc.primary);
  set('primary-foreground', bc.primaryForeground);
  set('background', bc.background);
  set('foreground', bc.text);
  set('card', bc.card);
  set('card-foreground', bc.text);
  set('popover', bc.popover);
  set('popover-foreground', bc.text);
  set('secondary', bc.card);
  set('secondary-foreground', bc.text);

  // Mode-aware derived colors
  if (mode === 'dark') {
    set('muted', bc.card);
    set('muted-foreground', bc.sidebarText);
    set('accent', bc.card);
    set('accent-foreground', bc.text);
    set('destructive', '#7f1d1d');
    set('destructive-foreground', '#FFFFFF');
    root.style.removeProperty('--color-border');
    root.style.removeProperty('--border');
    root.style.removeProperty('--color-input');
    root.style.removeProperty('--input');
  } else {
    set('muted', bc.background);
    set('muted-foreground', bc.sidebarText);
    set('accent', bc.background);
    set('accent-foreground', bc.text);
    set('destructive', '#F5222D');
    set('destructive-foreground', '#FFFFFF');
    root.style.removeProperty('--color-border');
    root.style.removeProperty('--border');
    root.style.removeProperty('--color-input');
    root.style.removeProperty('--input');
  }
  set('ring', bc.primary);

  // Sidebar variables
  set('sidebar', bc.sidebarBackground);
  set('sidebar-foreground', bc.sidebarText);
  set('sidebar-primary', bc.sidebarActive);
  set('sidebar-primary-foreground', bc.sidebarActiveText);
  set('sidebar-accent', bc.sidebarHover);
  set('sidebar-accent-foreground', bc.sidebarText);
  root.style.removeProperty('--color-sidebar-border');
  root.style.removeProperty('--sidebar-border');
  set('sidebar-ring', bc.primary);

  // Semantic colors
  set('success', '#22c55e');
  set('warning', '#eab308');
  set('info', bc.primary);
}

/**
 * Clear all inline style overrides so CSS @theme + .dark values take effect.
 * Called when using default branding (no custom tenant colors).
 */
function clearAllInlineOverrides(root: HTMLElement): void {
  const properties = [
    'primary', 'primary-foreground', 'background', 'foreground',
    'card', 'card-foreground', 'popover', 'popover-foreground',
    'secondary', 'secondary-foreground', 'muted', 'muted-foreground',
    'accent', 'accent-foreground', 'destructive', 'destructive-foreground',
    'border', 'input', 'ring',
    'sidebar', 'sidebar-foreground', 'sidebar-primary', 'sidebar-primary-foreground',
    'sidebar-accent', 'sidebar-accent-foreground', 'sidebar-border', 'sidebar-ring',
    'success', 'warning', 'info',
  ];
  for (const prop of properties) {
    root.style.removeProperty(`--color-${prop}`);
    root.style.removeProperty(`--${prop}`);  // Legacy cleanup — remove old --X form if present
  }
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: 'light' | 'dark';
}

export function ThemeProvider({ children, defaultMode = 'light' }: ThemeProviderProps) {
  const { branding } = useBranding();
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as 'light' | 'dark' | null;
    return saved || defaultMode;
  });
  const [currentPalette, setCurrentPalette] = useState<ColorPalette>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as 'light' | 'dark' | null;
    const initial = saved || defaultMode;
    return brandingToPalette(SYSTEM_DEFAULT_BRANDING, initial);
  });

  // When branding or mode changes, recompute palette + apply everything
  useEffect(() => {
    const activeBranding = branding.useDefaultTheme ? SYSTEM_DEFAULT_BRANDING : branding;
    const isUsingDefaults = branding.useDefaultTheme || activeBranding === SYSTEM_DEFAULT_BRANDING;
    const bc: BrandingColors = mode === 'dark' ? activeBranding.dark : activeBranding.light;

    // Update CSS variables (clears inline overrides when using defaults,
    // so the OKLCH values from index.css @theme + .dark take effect)
    applyCssVariables(bc, mode, isUsingDefaults);

    // Update document title
    if (activeBranding.portalTitle) {
      document.title = activeBranding.portalTitle;
    }

    // Update favicon — pass effectiveVersion so server URLs get a
    // cache-bust query string (W1c-08 hot-reload requirement).
    updateFavicon(activeBranding.faviconUrl, activeBranding.effectiveVersion);

    // Update palette state for context consumers
    setCurrentPalette(brandingToPalette(activeBranding, mode));

    // Persist mode preference
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode, branding]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const setCustomTheme = (palette: ColorPalette) => {
    setMode(palette.mode);
    setCurrentPalette(palette);
  };

  const resetToDefault = () => {
    setMode('light');
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <ThemeContext.Provider
      value={{
        mode,
        currentPalette,
        toggleTheme,
        setCustomTheme,
        resetToDefault,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
